import React from 'react'
import { AlertCircle, Check, Loader2, X } from 'lucide-react'
import type { TradeType } from './types'
import { PAYMENT_METHODS } from './tradePaymentMethods'
import { NativeModalDialog } from './NativeModalDialog'
import styles from '../CreateTransaction.module.css'

type PaymentModalProps = {
  open: boolean
  trade: TradeType
  formatPrice: (cents: number) => string
  selectedPaymentMethod: string
  onSelectPaymentMethod: (value: string) => void
  onClose: () => void
  onComplete: () => void
  loading: boolean
  /** True while a change to the customer offer (e.g. this payment method's rule %) is still saving. */
  offerUpdating?: boolean
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  trade,
  formatPrice,
  selectedPaymentMethod,
  onSelectPaymentMethod,
  onClose,
  onComplete,
  loading,
  offerUpdating = false,
}) => {
  const diff = trade.differenceValueCents
  const isEven = diff === 0
  const customerOwes = diff > 0

  return (
    <NativeModalDialog open={open} onClose={onClose} className={styles.nativeModalDialog}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Complete Transaction</h3>
          <button type="button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.tradeSummary}>
            <div className={styles.summaryRow}>
              <span>Store Items ({trade.storeItems?.length || 0})</span>
              <span>{formatPrice(trade.storeValueCents)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span>Customer Items ({trade.customerItems?.length || 0})</span>
              <span>{formatPrice(trade.customerValueCents)}</span>
            </div>
            <div className={`${styles.summaryRow} ${styles.difference}`}>
              <span>Difference</span>
              <span className={isEven ? styles.even : customerOwes ? styles.customerOwes : styles.storeOwes}>
                {isEven ? 'Even' : customerOwes ? `Customer owes ${formatPrice(diff)}` : `Store owes ${formatPrice(Math.abs(diff))}`}
              </span>
            </div>
          </div>

          {!isEven && (
            <div className={styles.paymentMethod}>
              <label>Payment Method</label>
              <div className={styles.paymentOptions}>
                {PAYMENT_METHODS.map((method) => (
                  <button
                    key={method.value}
                    type="button"
                    className={`${styles.paymentOption} ${selectedPaymentMethod === method.value ? styles.selected : ''}`}
                    onClick={() => onSelectPaymentMethod(method.value)}
                  >
                    <method.icon size={20} />
                    {method.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={styles.inventoryNotice}>
            <AlertCircle size={16} />
            <span>
              Completing this trade will {(trade.storeItems?.length || 0) > 0 && 'remove store items from inventory'}
              {(trade.storeItems?.length || 0) > 0 && (trade.customerItems?.length || 0) > 0 && ' and '}
              {(trade.customerItems?.length || 0) > 0 && 'add customer items to inventory'}
            </span>
          </div>
        </div>

        <div className={styles.modalActions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          {offerUpdating && <span className={styles.paymentOfferUpdating} aria-live="polite">Updating the customer offer…</span>}
          <button type="button" className={styles.confirmBtn} onClick={onComplete} disabled={loading || offerUpdating}>
            {loading ? <Loader2 className={styles.spinner} size={16} /> : <Check size={16} />}
            Complete Transaction
          </button>
        </div>
      </div>
    </NativeModalDialog>
  )
}
